import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { IoMdClose } from "react-icons/io";
import { IoChatbubbleEllipsesOutline, IoSend } from "react-icons/io5";
import { BsArrowLeft, BsSearch, BsCheck2All as TickIcon } from "react-icons/bs";
import { FiPaperclip } from "react-icons/fi";
import dummyimage from "../img/default_avatar.jpg";
import MyYuukke from "../api/MyYuukke";
import { toast } from "react-toastify";
import BlockUi from "react-block-ui";

class EnquiryChat extends Component {
  constructor(props) {
    super(props);
    this.state = {
      enquiries: [],
      messages: [],
      activeEnquiry: null,
      searchKey: "",
      filter: "all",
      draft: "",
      loading: false,
      chatLoading: false,
    };
    this.scrollRef = React.createRef();
    this.chatInterval = null;
  }

  // Safely extract the Database User ID from Redux
  getUserId = () => {
    return (
      this.props.loginDetails?.id ||
      this.props.loginDetails?.userId ||
      this.props.loginDetails?.uId ||
      this.props.authUser?.userId
    );
  };

  componentDidMount() {
    if (this.props.show) {
      this.loadEnquiries();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.loadEnquiries();
    }

    if (prevState.activeEnquiry !== this.state.activeEnquiry) {
      if (this.state.activeEnquiry) {
        this.loadChatHistory(this.state.activeEnquiry);
        // Start polling chat updates every 4 seconds when a window is active
        this.chatInterval = setInterval(() => {
          this.loadChatHistory(this.state.activeEnquiry, true);
        }, 4000);
      } else {
        if (this.chatInterval) clearInterval(this.chatInterval);
      }
    }

    if (prevState.messages.length !== this.state.messages.length) {
      this.scrollToBottom();
    }
  }

  componentWillUnmount() {
    if (this.chatInterval) clearInterval(this.chatInterval);
  }

  loadEnquiries = () => {
    const userId = this.getUserId();
    if (!userId) {
      console.error("EnquiryChat: Could not resolve User ID from Redux.");
      return;
    }

    this.setState({ loading: true });

    // Payload exactly matches Spring Boot expected keys
    const payload = { providerId: userId };

    MyYuukke.getProviderEnquiries(payload)
      .then((res) => {
        if (res?.data?.status === 1) {
          this.setState({ enquiries: res.data.data || [] });
          this.reportUnreadCount(res.data.data || []);
        } else {
          this.setState({ enquiries: [] });
        }
      })
      .catch((err) => console.error("Error loading enquiries:", err))
      .finally(() => this.setState({ loading: false }));
  };

  loadChatHistory = (enquiryId, isPolling = false) => {
    const userId = this.getUserId();
    if (!userId) return;

    if (!isPolling) this.setState({ chatLoading: true });

    const payload = { userId: userId };

    MyYuukke.getEnquiryChatHistory(enquiryId, payload)
      .then((res) => {
        if (res?.data?.status === 1) {
          this.setState({ messages: res.data.data || [] });
        }
      })
      .catch((err) => console.error("Error loading chat history:", err))
      .finally(() => {
        if (!isPolling) this.setState({ chatLoading: false });
      });
  };

  reportUnreadCount = (enquiriesList) => {
    if (typeof this.props.onUnreadCountChange === "function") {
      const total = enquiriesList.reduce((a, b) => a + (b.unread || 0), 0);
      this.props.onUnreadCountChange(total);
    }
  };

  scrollToBottom = () => {
    if (this.scrollRef.current) {
      this.scrollRef.current.scrollTop = this.scrollRef.current.scrollHeight;
    }
  };

  openEnquiry = (enquiry) => {
    this.setState({
      activeEnquiry: enquiry.id,
      draft: "",
    });
  };

  backToList = () => {
    this.setState({ activeEnquiry: null, messages: [] });
    this.loadEnquiries(); 
  };

  sendMessage = (e) => {
    if (e) e.preventDefault();
    const { draft, activeEnquiry, messages } = this.state;
    if (!draft.trim() || !activeEnquiry) return;

    const userId = this.getUserId();
    if (!userId) {
      toast.error("User session missing.");
      return;
    }

    const payload = {
      senderId: userId,
      enquiryId: activeEnquiry,
      message: draft.trim(),
    };

    MyYuukke.sendEnquiryChatMessage(payload)
      .then((res) => {
        if (res?.data?.status === 1) {
          this.setState({
            messages: [...messages, res.data.data],
            draft: "",
          });
          this.scrollToBottom();
        } else {
          toast.error(res?.data?.message || "Failed to deliver message");
        }
      })
      .catch((err) => {
        console.error("Error sending message:", err);
        toast.error("Network problem sending response");
      });
  };

  handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  };

  filteredList = () => {
    const { enquiries, searchKey, filter } = this.state;
    return enquiries
      .filter((e) => {
        if (filter === "all") return true;
        return e.status?.toLowerCase() === filter.toLowerCase();
      })
      .filter((e) => {
        const k = searchKey.trim().toLowerCase();
        if (!k) return true;
        return (
          (e.contactName || "").toLowerCase().includes(k) ||
          (e.subject || "").toLowerCase().includes(k) ||
          (e.message || "").toLowerCase().includes(k)
        );
      });
  };

  render() {
    const { t, show, handleClickAway } = this.props;
    const { activeEnquiry, draft, filter, searchKey, messages, enquiries, loading, chatLoading } = this.state;
    
    const currentUserId = this.getUserId();
    const list = this.filteredList();
    const active = enquiries.find((e) => e.id === activeEnquiry);
    const totalUnread = enquiries.reduce((a, b) => a + (b.unread || 0), 0);

    return (
      <>
        <style>{enquiryStyles}</style>

        {show && <div className="eq-backdrop" onClick={handleClickAway} />}

        <aside className={show ? "eq-panel eq-panel-open" : "eq-panel"}>
          {/* ---------- HEADER ---------- */}
          <header className="eq-header">
            <div className="eq-header-left">
              {active && (
                <button type="button" className="eq-icon-btn" onClick={this.backToList}>
                  <BsArrowLeft />
                </button>
              )}
              <div className="eq-header-title">
                <h5>
                  {active ? active.contactName : t("Incoming Requests")}
                  {!active && totalUnread > 0 && (
                    <span className="eq-total-badge">{totalUnread}</span>
                  )}
                </h5>
                <span className="eq-header-sub">
                  {active ? active.subject : t("Manage and respond to enquiries for your services")}
                </span>
              </div>
            </div>
            <button type="button" className="eq-icon-btn" onClick={handleClickAway}>
              <IoMdClose />
            </button>
          </header>

          {/* ---------- LIST VIEW ---------- */}
          {!active && (
            <BlockUi tag="div" blocking={loading} className="eq-list-view">
              <div className="eq-search">
                <BsSearch className="eq-search-icon" />
                <input
                  type="text"
                  placeholder={t("Search by client name, subject...")}
                  value={searchKey}
                  onChange={(e) => this.setState({ searchKey: e.target.value })}
                />
              </div>

              <div className="eq-filters">
                {["all", "new", "responded", "closed"].map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={filter === f ? "eq-chip eq-chip-active" : "eq-chip"}
                    onClick={() => this.setState({ filter: f })}
                  >
                    {t(f.toUpperCase())}
                  </button>
                ))}
              </div>

              <div className="eq-scroll">
                {list.length === 0 && (
                  <div className="eq-empty">
                    <IoChatbubbleEllipsesOutline />
                    <p>{t("No records found")}</p>
                  </div>
                )}

                {list.map((item) => (
                  <div
                    key={item.id}
                    className="eq-card"
                    onClick={() => this.openEnquiry(item)}
                  >
                    <div className="eq-avatar">
                      <img
                        src={dummyimage}
                        alt="client"
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null;
                          currentTarget.src = dummyimage;
                        }}
                      />
                      <span className={`eq-dot eq-dot-${item.status?.toLowerCase()}`} />
                    </div>

                    <div className="eq-card-body">
                      <div className="eq-card-top">
                        <h6>{item.contactName || t("Anonymous Client")}</h6>
                        <span className="eq-time">
                          {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : ""}
                        </span>
                      </div>
                      <p className="eq-subject">{item.entityName} • {item.subject}</p>
                      <div className="eq-card-bottom">
                        <p className="eq-preview">{item.message}</p>
                        {item.unread > 0 && (
                          <span className="eq-unread">{item.unread}</span>
                        )}
                      </div>
                      <span className={`eq-tag eq-tag-${item.status?.toLowerCase()}`}>
                        {item.entityType}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </BlockUi>
          )}

          {/* ---------- CHAT VIEW ---------- */}
          {active && (
            <BlockUi tag="div" blocking={chatLoading} className="eq-chat-view">
              <div className="eq-chat-scroll" ref={this.scrollRef}>
                <div className="eq-chat-meta">
                  <span className={`eq-tag eq-tag-${active.status?.toLowerCase()}`}>
                    {active.entityType}
                  </span>
                  <span className="eq-status-text">
                    {t("Status")}: <b className="text-dark">{active.status}</b>
                  </span>
                </div>

                {messages.map((m) => {
                  const isMine = m.isMine || m.senderId === currentUserId;
                  return (
                    <div
                      key={m.id}
                      className={isMine ? "eq-msg eq-msg-self" : "eq-msg"}
                    >
                      {!isMine && (
                        <img
                          className="eq-msg-avatar"
                          src={dummyimage}
                          alt="avatar"
                        />
                      )}
                      <div className="eq-bubble">
                        <p>{m.message}</p>
                        <span className="eq-msg-time">
                          {m.createdAt ? new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}
                          {isMine && <TickIcon className="eq-tick" style={{ color: "#4caf50" }} />}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="eq-composer">
                <button className="eq-icon-btn eq-attach" type="button">
                  <FiPaperclip />
                </button>
                <textarea
                  rows="1"
                  placeholder={t("Type your message here...")}
                  value={draft}
                  onChange={(e) => this.setState({ draft: e.target.value })}
                  onKeyDown={this.handleKeyDown}
                />
                <button
                  type="button"
                  className="eq-send"
                  onClick={this.sendMessage}
                  disabled={!draft.trim()}
                >
                  <IoSend />
                </button>
              </div>
            </BlockUi>
          )}
        </aside>
      </>
    );
  }
}

const enquiryStyles = `
.eq-backdrop{position:fixed;inset:0;background:rgba(20,25,30,.35);backdrop-filter:blur(2px);z-index:1090;animation:eqFade .25s ease;}
@keyframes eqFade{from{opacity:0}to{opacity:1}}

.eq-panel{position:fixed;top:0;right:0;height:100vh;width:450px;max-width:100vw;background:#fff;z-index:1100;display:flex;flex-direction:column;
  box-shadow:-14px 0 40px rgba(0,0,0,.14);transform:translateX(105%);transition:transform .35s cubic-bezier(.22,.61,.36,1);
  font-family:inherit;}
.eq-panel-open{transform:translateX(0);}
@media(max-width:600px){.eq-panel{width:100%;}}

.eq-header{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:16px 14px;
  background:linear-gradient(135deg,#A00030 0%,#c9134a 100%);color:#fff;flex-shrink:0;}
.eq-header-left{display:flex;align-items:center;gap:8px;min-width:0;}
.eq-header-title h5{margin:0;font-size:1.05rem;font-weight:600;color:#fff;display:flex;align-items:center;gap:8px;}
.eq-header-sub{font-size:.72rem;opacity:.85;display:block;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:270px;}
.eq-total-badge{background:#fff;color:#A00030;border-radius:20px;font-size:.68rem;font-weight:700;padding:1px 7px;}
.eq-icon-btn{background:rgba(255,255,255,.16);border:none;color:#fff;width:32px;height:32px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;font-size:1.05rem;cursor:pointer;transition:.2s;flex-shrink:0;}
.eq-icon-btn:hover{background:rgba(255,255,255,.3);}

.eq-list-view,.eq-chat-view{display:flex;flex-direction:column;flex:1;min-height:0;height:100%;}

.eq-search{position:relative;margin:14px 14px 8px;}
.eq-search input{width:100%;border:1px solid #e6e8ec;background:#f7f8fa;border-radius:30px;padding:9px 14px 9px 36px;font-size:.82rem;outline:none;transition:.2s;}
.eq-search input:focus{border-color:#A00030;background:#fff;box-shadow:0 0 0 3px rgba(160,0,48,.08);}
.eq-search-icon{position:absolute;left:14px;top:50%;transform:translateY(-50%);color:#9aa0a6;font-size:.85rem;}

.eq-filters{display:flex;gap:6px;padding:0 14px 10px;flex-wrap:wrap;}
.eq-chip{border:1px solid #e6e8ec;background:#fff;color:#676a79;border-radius:20px;padding:4px 12px;font-size:.72rem;cursor:pointer;transition:.2s;}
.eq-chip:hover{border-color:#A00030;color:#A00030;}
.eq-chip-active{background:#A00030;border-color:#A00030;color:#fff;}

.eq-scroll{flex:1;overflow-y:auto;overflow-x:hidden;padding:2px 10px 20px;}
.eq-scroll::-webkit-scrollbar,.eq-chat-scroll::-webkit-scrollbar{width:5px;}
.eq-scroll::-webkit-scrollbar-thumb,.eq-chat-scroll::-webkit-scrollbar-thumb{background:#dcdfe4;border-radius:10px;}

.eq-card{display:flex;gap:10px;padding:12px;border-radius:12px;cursor:pointer;transition:.2s;position:relative;border:1px solid transparent;}
.eq-card:hover{background:#fdf3f6;border-color:#f2d7e0;transform:translateY(-1px);}
.eq-avatar{position:relative;flex-shrink:0;}
.eq-avatar img{width:44px;height:44px;border-radius:50%;object-fit:cover;border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.12);}
.eq-dot{position:absolute;right:0;bottom:2px;width:11px;height:11px;border-radius:50%;border:2px solid #fff;}
.eq-dot-new{background:#22c55e;}
.eq-dot-responded{background:#f59e0b;}
.eq-dot-closed{background:#9aa0a6;}

.eq-card-body{flex:1;min-width:0;}
.eq-card-top{display:flex;justify-content:space-between;align-items:center;gap:6px;}
.eq-card-top h6{margin:0;font-size:.86rem;font-weight:600;color:#14191e;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.eq-time{font-size:.68rem;color:#9aa0a6;flex-shrink:0;}
.eq-subject{margin:2px 0 3px;font-size:.76rem;color:#A00030;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.eq-card-bottom{display:flex;align-items:center;justify-content:space-between;gap:6px;}
.eq-preview{margin:0;font-size:.74rem;color:#676a79;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.eq-unread{background:#A00030;color:#fff;font-size:.65rem;font-weight:700;min-width:18px;height:18px;border-radius:20px;
  display:flex;align-items:center;justify-content:center;padding:0 5px;flex-shrink:0;}
.eq-tag{display:inline-block;margin-top:6px;font-size:.62rem;font-weight:600;padding:2px 8px;border-radius:20px;letter-spacing:.3px;text-transform:uppercase;}
.eq-tag-new{background:#e8f8ee;color:#15803d;}
.eq-tag-responded{background:#fff4e5;color:#b45309;}
.eq-tag-closed{background:#f1f2f4;color:#676a79;}

.eq-empty{text-align:center;padding:60px 20px;color:#9aa0a6;}
.eq-empty svg{font-size:2.6rem;margin-bottom:10px;opacity:.5;}
.eq-empty p{font-size:.8rem;margin:0;}

.eq-chat-scroll{flex:1;overflow-y:auto;padding:16px 14px;background:#f7f8fa;}
.eq-chat-meta{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;}
.eq-status-text{font-size:.68rem;color:#9aa0a6;}

.eq-msg{display:flex;align-items:flex-end;gap:8px;margin-bottom:12px;animation:eqUp .25s ease;}
@keyframes eqUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.eq-msg-self{flex-direction:row-reverse;}
.eq-msg-avatar{width:28px;height:28px;border-radius:50%;object-fit:cover;flex-shrink:0;}
.eq-bubble{max-width:76%;background:#fff;border-radius:14px 14px 14px 4px;padding:9px 12px 6px;box-shadow:0 1px 3px rgba(0,0,0,.07);color:#333;}
.eq-msg-self .eq-bubble{background:linear-gradient(135deg,#A00030,#c9134a);color:#fff;border-radius:14px 14px 4px 14px;}
.eq-bubble p{margin:0;font-size:.8rem;line-height:1.45;word-break:break-word;white-space:pre-wrap;}
.eq-msg-time{display:flex;align-items:center;justify-content:flex-end;font-size:0.62rem;color:#9aa0a6;margin-top:3px;}
.eq-msg-self .eq-msg-time{color:rgba(255,255,255,.75);}
.eq-tick{margin-left:4px;font-size:.72rem;}

.eq-composer{display:flex;align-items:flex-end;gap:8px;padding:10px 12px;border-top:1px solid #eceef1;background:#fff;flex-shrink:0;}
.eq-composer textarea{flex:1;resize:none;max-height:110px;border:1px solid #e6e8ec;border-radius:18px;padding:9px 14px;
  font-size:.82rem;outline:none;background:#f7f8fa;transition:.2s;line-height:1.4;}
.eq-composer textarea:focus{border-color:#A00030;background:#fff;box-shadow:0 0 0 3px rgba(160,0,48,.08);}
.eq-attach{background:#f1f2f4;color:#676a79;}
.eq-attach:hover{background:#e6e8ec;}
.eq-send{width:38px;height:38px;border-radius:50%;border:none;background:#A00030;color:#fff;display:flex;
  align-items:center;justify-content:center;font-size:1rem;cursor:pointer;transition:.2s;flex-shrink:0;}
.eq-send:hover:not(:disabled){background:#c9134a;transform:scale(1.06);}
.eq-send:disabled{opacity:.45;cursor:not-allowed;}
`;

const mapStateToProps = (state) => ({
  loginDetails: state.loginDetailsApi.loginDetails,
  authUser: state.loginDetailsApi.authUser,
  workspaceName: state.loginDetailsApi.loginDetails.userWorkSpace?.slug,
});

export default connect(mapStateToProps)(
  withTranslation()(withRouter(EnquiryChat))
);